// Purpose: Utility functions for handling timeouts consistently across the application

/**
 * Creates a Promise that rejects after the specified timeout
 * @param timeoutMs - Timeout duration in milliseconds
 * @param errorMessage - Optional custom error message
 * @returns Promise that rejects with timeout error
 */
export function createTimeoutPromise(timeoutMs: number, errorMessage?: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(errorMessage || `Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

/**
 * Wraps a Promise with a timeout, using Promise.race()
 * @param promise - The Promise to wrap with timeout
 * @param timeoutMs - Timeout duration in milliseconds
 * @param errorMessage - Optional custom error message for timeout
 * @returns Promise that resolves with the original result or rejects with timeout error
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage?: string
): Promise<T> {
  return Promise.race([
    promise,
    createTimeoutPromise(timeoutMs, errorMessage)
  ]);
}

/**
 * Common timeout durations used throughout the application
 */
export const TIMEOUT_DURATIONS = {
  RECAPTCHA_SCRIPT_LOAD: 30000,    // 30 seconds for reCAPTCHA script loading
  RECAPTCHA_EXECUTION: 15000,      // 15 seconds for reCAPTCHA token generation
  LOGO_PRELOAD: 10000,             // 10 seconds for logo image preloading
  API_REQUEST: 30000,              // 30 seconds for API requests
  FILE_UPLOAD: 60000,              // 60 seconds for file uploads
} as const;

/**
 * Creates a timeout-wrapped Promise specifically for reCAPTCHA operations
 * @param promise - The reCAPTCHA Promise to wrap
 * @param operation - The type of reCAPTCHA operation (for error messaging)
 * @returns Promise with appropriate timeout and error message
 */
export function withRecaptchaTimeout<T>(
  promise: Promise<T>,
  operation: 'script-load' | 'execution'
): Promise<T> {
  const timeoutMs = operation === 'script-load' 
    ? TIMEOUT_DURATIONS.RECAPTCHA_SCRIPT_LOAD 
    : TIMEOUT_DURATIONS.RECAPTCHA_EXECUTION;
  
  const errorMessage = operation === 'script-load'
    ? `reCAPTCHA script load timeout after ${timeoutMs / 1000} seconds`
    : `reCAPTCHA execution timeout after ${timeoutMs / 1000} seconds`;

  return withTimeout(promise, timeoutMs, errorMessage);
}

/**
 * Creates a timeout-wrapped Promise specifically for image loading operations
 * @param promise - The image loading Promise to wrap
 * @param timeoutMs - Optional custom timeout (defaults to LOGO_PRELOAD)
 * @returns Promise with appropriate timeout and error message
 */
export function withImageTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number = TIMEOUT_DURATIONS.LOGO_PRELOAD
): Promise<T> {
  return withTimeout(
    promise,
    timeoutMs,
    `Image preload timeout after ${timeoutMs / 1000} seconds`
  );
}

/**
 * Retry configuration for operations
 */
export const RETRY_CONFIG = {
  RECAPTCHA_LOAD: {
    maxAttempts: 3,
    baseDelayMs: 1000,
    maxDelayMs: 5000,
  },
  RECAPTCHA_EXECUTION: {
    maxAttempts: 2,
    baseDelayMs: 500,
    maxDelayMs: 2000,
  },
  IMAGE_LOAD: {
    maxAttempts: 2,
    baseDelayMs: 1000,
    maxDelayMs: 3000,
  },
} as const;

/**
 * Creates a delay Promise for retry logic
 * @param ms - Delay duration in milliseconds
 * @returns Promise that resolves after the specified delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculates exponential backoff delay with jitter
 * @param attempt - Current attempt number (1-based)
 * @param baseDelayMs - Base delay in milliseconds
 * @param maxDelayMs - Maximum delay in milliseconds
 * @returns Calculated delay with jitter
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number
): number {
  // Exponential backoff: baseDelay * 2^(attempt-1)
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt - 1);
  
  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);
  
  // Add jitter (±25% randomness to prevent thundering herd)
  const jitter = cappedDelay * 0.25 * (Math.random() - 0.5);
  
  return Math.round(cappedDelay + jitter);
}

/**
 * Executes a function with retry logic and exponential backoff
 * @param fn - Function to execute (should return a Promise)
 * @param config - Retry configuration
 * @param context - Context for logging (e.g., 'reCAPTCHA Load')
 * @returns Promise that resolves with the result or rejects after all attempts fail
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: typeof RETRY_CONFIG.RECAPTCHA_LOAD,
  context: string
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      const result = await fn();
      
      if (attempt > 1) {
        // Log successful retry
        console.log(`[${context}] Succeeded on attempt ${attempt}/${config.maxAttempts}`);
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      console.warn(`[${context}] Attempt ${attempt}/${config.maxAttempts} failed:`, error);
      
      // If this was the last attempt, don't delay
      if (attempt === config.maxAttempts) {
        break;
      }
      
      // Calculate and apply backoff delay
      const delayMs = calculateBackoffDelay(attempt, config.baseDelayMs, config.maxDelayMs);
      console.log(`[${context}] Retrying in ${delayMs}ms...`);
      
      await delay(delayMs);
    }
  }

  // All attempts failed, throw the last error
  throw new Error(
    `${context} failed after ${config.maxAttempts} attempts. Last error: ${
      lastError instanceof Error ? lastError.message : String(lastError)
    }`
  );
}
